import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useEventApi } from "src/_common/hooks/actions/event/appEventApiHook";
import { handleApiCall, URLS, DATE_FORMAT, API_BASE_URL, ACTIONS } from "src/_config";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useHistory, Link } from "react-router-dom";
import { useAppUserDetailsSelector, useAppUserTokenSelector } from "src/_common/hooks/selectors/userSelector";
import DateInput from "src/_common/components/form-elements/datepicker/dateInput";
import moment from "moment";
import axios from "axios";
import { useDispatch } from "react-redux";
import ProfileImageUpdateModal from "../view-profile/ProfileImageUpdateModal";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface MemberEditProfileFormValues {
  // Personal Details
  name: string;
  email: string;
  mobileNumber: string;
  countryCode: string;
  designation: string;
  employeeCode: string;
  password: string;
  gender: string;
  date_of_birth: Date | undefined;
  marital_status: string;
  religion: string;
  home_town: string;
  nationality: string;
  language: string;
  permanent_address: string;
  current_address: string;
  two_factor_auth: boolean;
}

const memberEditProfileSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Please enter a valid email").required("Email is required"),
  mobileNumber: yup.string().required("Mobile number is required"),
  designation: yup.string().required("Designation is required"),
  employeeCode: yup.string().required("Employee code is required"),
  password: yup
    .string()
    .transform((value) => (value === "" ? undefined : value))
    .min(6, "Password must be at least 6 characters")
    .notRequired(),
  gender: yup.string().required("Gender is required"),
  date_of_birth: yup.date().required("Date of birth is required").nullable(),
  marital_status: yup.string().required("Marital status is required"),
  religion: yup.string().required("Religion is required"),
  home_town: yup.string().required("Home town is required"),
  nationality: yup.string().required("Nationality is required"),
  language: yup.string().required("Language is required"),
  permanent_address: yup.string().required("Permanent address is required"),
  current_address: yup.string().required("Current address is required"),
});

interface MemberEditProfileProps {
  viewType?: string;
}

const MemberEditProfile: React.FC<MemberEditProfileProps> = ({ viewType = "personal" }) => {
  const dispatch = useDispatch();
  const userApi = useUserApi();
  const eventApi = useEventApi();
  const toast = useToaster();
  const userSelector = useAppUserDetailsSelector();
  const userToken = useAppUserTokenSelector();
  const { t } = useTranslation("member");
  const fileInputRef = useRef<HTMLInputElement>(null);

  //   const [countries, setCountries] = useState<Array<{ id: string; name: string }>>([]);
  const [languages, setLanguages] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState<boolean>(false);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState<boolean>(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isUpdatingTwoFactor, setIsUpdatingTwoFactor] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showImageUpdateModal, setShowImageUpdateModal] = useState(false);
  const history = useHistory();

  // Designation autocomplete states
  const [showDesignationDropdown, setShowDesignationDropdown] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [designations, setDesignations] = useState<Array<{ id: string; name: string }>>([]);
  const [filteredDesignations, setFilteredDesignations] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingDesignations, setIsLoadingDesignations] = useState<boolean>(false);
  const [designationSearchValue, setDesignationSearchValue] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState<string>("us");
  const [phoneCode, setPhoneCode] = useState<string>(""); // State to track phone country code

  // Refs
  const designationRef = useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<MemberEditProfileFormValues>({
    resolver: yupResolver(memberEditProfileSchema),
    defaultValues: {
      name: "",
      email: "",
      mobileNumber: "",
      countryCode: "+1",
      designation: "",
      employeeCode: "",
      password: "",
      gender: "",
      date_of_birth: undefined,
      marital_status: "",
      religion: "",
      home_town: "",
      nationality: "",
      language: "",
      permanent_address: "",
      current_address: "",
      two_factor_auth: false,
    },
  });

  // Load countries for nationality dropdown
  //   const loadCountries = () => {
  //     setIsLoadingCountries(true);
  //     handleApiCall(
  //       eventApi.callGetCountriesNew,
  //       { limit: 100, offset: 0 },
  //       (resp: any) => {
  //         let countriesData = null;

  //         if (resp?.countries && Array.isArray(resp.countries)) {
  //           countriesData = resp.countries;
  //         } else if (resp?.data && Array.isArray(resp.data)) {
  //           countriesData = resp.data;
  //         } else if (resp && Array.isArray(resp)) {
  //           countriesData = resp;
  //         } else if (resp?.result && Array.isArray(resp.result)) {
  //           countriesData = resp.result;
  //         }

  //         if (countriesData && countriesData.length > 0) {
  //           const formattedCountries = countriesData.map((country: any) => ({
  //             id: country.id?.toString() || country.country_id?.toString() || country.value?.toString() || "",
  //             name: country.name || country.country_name || country.label || country,
  //           }));
  //           setCountries(formattedCountries);
  //         } else {
  //           setCountries([]);
  //         }
  //         setIsLoadingCountries(false);
  //       },
  //       toast,
  //       false,
  //       true
  //     );
  //   };

  // Load designations
  const loadDesignations = () => {
    setIsLoadingDesignations(true);
    handleApiCall(
      eventApi.callGetDesignations,
      {},
      (resp: any) => {
        console.log("Designations API response:", resp);
        let formattedDesignations: Array<{ id: string; name: string }> = [];

        if (resp?.designations && Array.isArray(resp.designations)) {
          formattedDesignations = resp.designations.map((designation: any) => ({
            id: designation.id?.toString() || "",
            name: designation.name || "",
          }));
        } else if (resp && Array.isArray(resp)) {
          formattedDesignations = resp.map((designation: any) => ({
            id: designation.id?.toString() || "",
            name: designation.name || "",
          }));
        } else if (resp?.data && Array.isArray(resp.data)) {
          formattedDesignations = resp.data.map((designation: any) => ({
            id: (designation.id || designation.designation_id)?.toString() || "",
            name: designation.name || designation.designation_name || "",
          }));
        }

        console.log("Setting designations:", formattedDesignations);
        setDesignations(formattedDesignations);
        setFilteredDesignations(formattedDesignations);
        setIsLoadingDesignations(false);
      },
      toast,
      false,
      true
    );
  };

  const handleDesignationInputChange = (value: string, onChange: Function) => {
    onChange(value);
    setDesignationSearchValue(value);
    setShowDesignationDropdown(false);

    if (value.length > 0) {
      const filtered = designations.filter((designation) =>
        designation.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredDesignations(filtered);
      setShowAutocomplete(true);
    } else {
      setFilteredDesignations(designations);
      setShowAutocomplete(false);
    }
  };

  const handleDesignationSelect = (designation: string, onChange: Function) => {
    onChange(designation);
    setDesignationSearchValue(designation);
    setShowAutocomplete(false);
    setShowDesignationDropdown(false);
  };

  const handleDesignationFocus = () => {
    if (designations.length > 0) {
      setFilteredDesignations(designations);
      setShowAutocomplete(true);
      setShowDesignationDropdown(false);
    }
  };

  const handleDesignationBlur = () => {
    setTimeout(() => {
      setShowAutocomplete(false);
    }, 200);
  };

  // Load languages
  const loadLanguages = () => {
    setIsLoadingLanguages(true);
    // Mock languages for now - replace with actual API call if available
    const mockLanguages = [
      { id: "1", name: "English" },
      { id: "2", name: "Bengali" },
      { id: "3", name: "Hindi" },
      { id: "4", name: "Arabic" },
      { id: "5", name: "Spanish" },
      { id: "6", name: "French" },
      { id: "7", name: "German" },
      { id: "8", name: "Chinese" },
      { id: "9", name: "Japanese" },
      { id: "10", name: "Korean" },
    ];
    setLanguages(mockLanguages);
    setIsLoadingLanguages(false);
  };

  // Handle profile image click
  const handleProfileImageClick = () => {
    setShowImageUpdateModal(true);
  };

  // Handle confirm update
  const handleConfirmUpdate = () => {
    setShowImageUpdateModal(false);
    fileInputRef.current?.click();
  };

  // Handle cancel update
  const handleCancelUpdate = () => {
    setShowImageUpdateModal(false);
  };

  // Handle file selection for profile image
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error(t("members.profile.viewProfile.messages.invalidImage"));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("members.profile.viewProfile.messages.imageSizeError"));
        return;
      }

      uploadProfileImage(file);
    }
  };

  // Upload profile image API call
  const uploadProfileImage = async (file: File) => {
    setIsUploading(true);

    try {
      // Check if user is authenticated
      if (!userToken) {
        toast.error(t("members.profile.viewProfile.messages.loginRequired"));
        setIsUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append("profile_image", file);

      // Get auth token from Redux store
      const token = userToken;

      const response = await axios.post(`${API_BASE_URL}upload-profile-image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.status === 200) {
        // Show success message from API
        toast.success(response.data.message || t("members.profile.viewProfile.messages.uploadSuccess"));

        // Update profile image in Redux state immediately
        if (response.data.data?.profile_image_url) {
          // Update user data in Redux with new profile image
          const updatedUser = {
            ...userSelector,
            profile_image: response.data.data.profile_image_url,
          };

          dispatch({
            type: ACTIONS.USER.ME,
            payload: updatedUser,
          });
        }

        // Refresh user data to get updated profile image
        userMeCall();
      } else {
        toast.error(t("members.profile.viewProfile.messages.uploadError"));
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t("members.profile.viewProfile.messages.uploadError"));
      }
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Fetch user profile data and populate form
  const userMeCall = () => {
    handleApiCall(userApi.callGetMe, null, () => {}, toast, false, true);
  };

  // Handle two-factor authentication toggle
  const handleToggleTwoFactor = (newStatus: boolean) => {
    console.log("Toggle two-factor to:", newStatus);
    setIsUpdatingTwoFactor(true);

    const requestData = {
      two_fa_auth_enabled: newStatus,
    };

    handleApiCall(
      userApi.callUpdateTwoFactorAuth,
      requestData,
      (response: any) => {
        console.log("Two-factor update response:", response);
        setTwoFactorEnabled(newStatus);
        setValue("two_factor_auth", newStatus);
        setIsUpdatingTwoFactor(false);
        userMeCall();
      },
      toast,
      true,
      false
    );
  };

  const handleMemberFormSubmit = (values: any) => {
    // Use the captured country code from state or form values
    const countryCode = values.countryCode || phoneCode || "";
    const fullPhoneNumber = values.mobileNumber || "";

    console.log("MemberEditProfile handleMemberFormSubmit - Full phone number:", fullPhoneNumber);
    console.log("MemberEditProfile handleMemberFormSubmit - Country code from form:", values.countryCode);
    console.log("MemberEditProfile handleMemberFormSubmit - Country code from state:", phoneCode);
    console.log("MemberEditProfile handleMemberFormSubmit - Final country code:", countryCode);

    // Extract phone number without country code
    const phoneDigitsOnly = fullPhoneNumber.toString().replace(/\D/g, "");
    const countryCodeDigits = countryCode.replace(/\D/g, "");

    let mobile = phoneDigitsOnly;
    if (countryCodeDigits && phoneDigitsOnly.startsWith(countryCodeDigits)) {
      mobile = phoneDigitsOnly.substring(countryCodeDigits.length);
    }

    console.log("MemberEditProfile handleMemberFormSubmit - Extracted mobile:", mobile);
    console.log("MemberEditProfile handleMemberFormSubmit - phone_code:", countryCode);

    // Prepare form data for member API
    const formData = new FormData();

    formData.append("name", values.name);
    formData.append("email", values.email);
    formData.append("contact_no", mobile);
    formData.append("contact_number", mobile);
    formData.append("phone_code", countryCode);
    formData.append("designation", values.designation);
    formData.append("employee_code", values.employeeCode);
    if (values.password) {
      formData.append("password", values.password);
    }
    formData.append("gender", values.gender);
    formData.append("date_of_birth", values.date_of_birth ? moment(values.date_of_birth).format("YYYY-MM-DD") : "");
    formData.append("marital_status", values.marital_status);
    formData.append("religion", values.religion);
    formData.append("home_town", values.home_town);
    formData.append("nationality", values.nationality);
    formData.append("language", values.language);
    formData.append("permanent_address", values.permanent_address);
    formData.append("current_address", values.current_address);

    handleApiCall(
      userApi.callUpdateUserProfile,
      formData,
      (response: any) => {
        history.push(`${URLS.AUTH.VIEW_PROFILE}${viewType === "company" ? "?view=company" : ""}`);
      },
      toast,
      true,
      false
    );
  };

  // Populate form with existing user data
  useEffect(() => {
    if (userSelector) {
      // Set two-factor authentication status from user object
      const twoFactorStatus = (userSelector as any)?.user?.two_fa_auth_enabled || false;
      console.log("Two-factor status from userSelector.user:", twoFactorStatus);
      console.log("Full userSelector object:", userSelector);
      setValue("two_factor_auth", twoFactorStatus);
      setTwoFactorEnabled(twoFactorStatus);

      // Populate member-specific fields
      if (userSelector?.member) {
        setValue("name", userSelector?.member?.name || "");
        setValue("email", userSelector?.member?.email || "");

        // Handle mobile number with country code
        if (userSelector?.member?.contact_number) {
          // Get the phone_code from member data
          const userPhoneCode = userSelector?.member?.phone_code || "";
          const contactNumber = userSelector.member.contact_number;

          // Remove any non-digit characters
          const cleanContactNumber = contactNumber.toString().replace(/\D/g, "");

          // If phone_code exists, use it; otherwise try to determine from contact_number length
          let fullPhoneNumber = cleanContactNumber;
          let detectedCountryCode = userPhoneCode;

          if (userPhoneCode) {
            // We have the country code, combine it with the contact number
            const codeDigits = userPhoneCode.replace(/\D/g, "");
            fullPhoneNumber = codeDigits + cleanContactNumber;
          } else {
            // No country code available, assume US +1 for 10-digit numbers
            if (cleanContactNumber.length === 10) {
              fullPhoneNumber = "1" + cleanContactNumber;
              detectedCountryCode = "+1";
            }
          }

          console.log(
            `MemberEditProfile loading data - phone_code: ${userPhoneCode}, contact_number: ${contactNumber}, fullPhoneNumber: ${fullPhoneNumber}`
          );

          // Set both the form value and state
          setValue("mobileNumber", fullPhoneNumber);
          setValue("countryCode", detectedCountryCode);
          setPhoneCode(detectedCountryCode);
        }

        setValue("designation", userSelector?.member?.designation || "");
        setValue("employeeCode", userSelector?.member?.employee_code || "");
        setValue("gender", userSelector?.member?.gender || "");

        // Handle date of birth conversion from ISO string to Date
        if (userSelector?.member?.date_of_birth) {
          setValue("date_of_birth", new Date(userSelector.member.date_of_birth));
        }

        setValue("marital_status", userSelector?.member?.marital_status || "");
        setValue("religion", userSelector?.member?.religion || "");
        setValue("home_town", userSelector?.member?.home_town || "");
        setValue("nationality", userSelector?.member?.nationality || "");
        setValue("language", userSelector?.member?.language || "");
        setValue("permanent_address", userSelector?.member?.permanent_address || "");
        setValue("current_address", userSelector?.member?.current_address || "");
      }
    }
  }, [userSelector]);

  // Handle click outside for designation dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (designationRef.current && !designationRef.current.contains(event.target as Node)) {
        setShowDesignationDropdown(false);
        setShowAutocomplete(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Load master data and refresh user data on mount
  useEffect(() => {
    // loadCountries();
    // loadLanguages();
    loadDesignations();
    // Fetch fresh user data to ensure we have the latest two-factor status
    userMeCall();
  }, []);

  return (
    <>
      <form id="member-profile-form" onSubmit={handleSubmit(handleMemberFormSubmit)} className="edit-profile-form">
        {/* Profile Header */}
        <div className="profile-header-card mb-4">
          <div className="d-flex align-items-center">
            <div className="profile-avatar me-4" style={{ position: "relative", cursor: "pointer" }}>
              <img
                src={userSelector?.member?.profile_photo_url || "/images/user-avatar.jpg"}
                alt="Profile Avatar"
                className="profile-avatar-img"
                onClick={handleProfileImageClick}
                style={{
                  opacity: isUploading ? 0.7 : 1,
                  transition: "opacity 0.3s ease",
                }}
              />
              {isUploading && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "#007bff",
                    fontSize: "20px",
                  }}
                >
                  <i className="fa fa-spinner fa-spin"></i>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleFileSelect}
              />
            </div>
            <div className="profile-basic-info flex-grow-1">
              <h2 className="profile-name mb-1">{userSelector?.member?.name || ""}</h2>
              <p className="profile-designation mb-3">{userSelector?.member?.designation || ""}</p>
            </div>
            <div className="profile-contact-info">
              {/* <button
              type="button"
              className="btn btn-preview"
              onClick={() => {
                // Add delete account functionality here
              }}
            >
              <i className="fa fa-trash-o me-2" aria-hidden="true"></i>
              Delete Account
            </button> */}
              <div className="contact-item mb-2">
                <span className="contact-label">{t("members.profile.viewProfile.contactInfo.phoneNumber")}</span>
                <span className="contact-value ms-2">
                  {userSelector?.user?.phone_code && userSelector?.user?.mobile_number
                    ? `${userSelector?.user?.phone_code} ${userSelector?.user?.mobile_number}`
                    : t("members.profile.personalInfo.notProvided")}
                </span>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("members.profile.viewProfile.contactInfo.email")}</span>
                <span className="contact-value ms-2">
                  {userSelector?.user?.email || t("members.profile.personalInfo.notProvided")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Personal Details Section */}
        <div className="form-section">
          <div className="section-header">
            <h2 className="section-title">{t("members.profile.memberEditProfile.title")}</h2>
            <p className="section-subtitle">{t("members.profile.memberEditProfile.subtitle")}</p>
          </div>

          <div className="form-grid">
            {/* Name */}
            <div className="form-group">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.name")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="name"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <input
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    type="text"
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    placeholder={t("members.profile.memberEditProfile.placeholders.name")}
                  />
                )}
              />
              {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
            </div>

            {/* Mobile */}
            <div className="form-group">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.mobile")} <span className="mandatory-mark">*</span>
              </label>
              <div className="figma-phone-wrapper">
                <Controller
                  control={control}
                  name="mobileNumber"
                  render={({ onChange, onBlur, value }) => (
                    <PhoneInput
                      country={"us"}
                      value={value || ""}
                      onChange={(phone: string, country: any) => {
                        console.log("MemberEditProfile PhoneInput onChange:", phone, "dialCode:", country.dialCode);
                        onChange(phone);
                        const code = `+${country.dialCode}`;
                        setValue("countryCode", code);
                        setPhoneCode(code);
                        console.log("MemberEditProfile phone_code set to:", code);
                      }}
                      onBlur={onBlur}
                      placeholder={t("members.profile.memberEditProfile.placeholders.mobile")}
                      inputClass={`figma-phone-input ${errors.mobileNumber ? "error" : ""}`}
                      containerClass="phone-input-container"
                    />
                  )}
                />
              </div>
              {errors.mobileNumber && <div className="invalid-feedback">{errors.mobileNumber.message}</div>}
            </div>

            {/* Email */}
            <div className="form-group">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.email")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="email"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <input
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    type="email"
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    placeholder={t("members.profile.memberEditProfile.placeholders.email")}
                  />
                )}
              />
              {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
            </div>

            {/* Designation */}
            <div className="form-group designation-autocomplete">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.designation")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                control={control}
                name="designation"
                render={({ onChange, value }) => (
                  <div className="figma-autocomplete-container" ref={designationRef}>
                    <input
                      type="text"
                      value={value || ""}
                      onChange={(e) => handleDesignationInputChange(e.target.value, onChange)}
                      onFocus={handleDesignationFocus}
                      onBlur={handleDesignationBlur}
                      placeholder={t("members.profile.memberEditProfile.placeholders.designation")}
                      className={`form-control ${errors.designation ? "is-invalid" : ""}`}
                      autoComplete="off"
                    />
                    {showAutocomplete && filteredDesignations.length > 0 && !showDesignationDropdown && (
                      <div className="figma-autocomplete-dropdown">
                        {filteredDesignations.map((designation) => (
                          <div
                            key={designation.id}
                            className="figma-autocomplete-option"
                            onClick={() => handleDesignationSelect(designation.name, onChange)}
                          >
                            {designation.name}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* <button
                      type="button"
                      className="figma-example-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("Example button clicked, designations:", designations);
                        setShowDesignationDropdown(!showDesignationDropdown);
                        setShowAutocomplete(false);
                      }}
                    >
                      {t("members.profile.memberEditProfile.buttons.example")}
                    </button> */}
                    <span className="example-text">
                      <b>Example:</b> AI Developer
                    </span>

                    {showDesignationDropdown && (
                      <div className="designation-example-dropdown">
                        <div className="designation-options-grid">
                          {designations.length > 0 ? (
                            designations.map((designation) => (
                              <button
                                key={designation.id}
                                type="button"
                                className="designation-option-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onChange(designation.name);
                                  setShowDesignationDropdown(false);
                                  setShowAutocomplete(false);
                                }}
                              >
                                {designation.name} +
                              </button>
                            ))
                          ) : (
                            <div style={{ padding: "10px", color: "#666" }}>
                              {isLoadingDesignations
                                ? t("members.profile.memberEditProfile.loading.designations")
                                : t("members.profile.memberEditProfile.loading.noDesignations")}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              />
              {errors.designation && <div className="invalid-feedback">{errors.designation.message}</div>}
            </div>

            {/* Password */}
            <div className="form-group">
              <label className="form-label">{t("members.profile.memberEditProfile.fields.password")}</label>
              <div className="password-input-container" style={{ position: "relative" }}>
                <Controller
                  name="password"
                  control={control}
                  render={({ onChange, onBlur, value }) => (
                    <input
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value || ""}
                      type={showPassword ? "text" : "password"}
                      className={`form-control ${errors.password ? "is-invalid" : ""}`}
                      placeholder={t("members.profile.memberEditProfile.placeholders.password")}
                      style={{ paddingRight: "50px" }}
                    />
                  )}
                />
                {/* <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                  color: "#666",
                }}
              >
                <i className={showPassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
              </button> */}
              </div>
              {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
            </div>

            {/* 2-Factor Authentication */}
            <div className="form-group">
              <label className="form-label">{t("members.profile.memberEditProfile.fields.twoFactorAuth")}</label>
              <Controller
                name="two_factor_auth"
                control={control}
                render={({ onChange, value }) => (
                  <div className="two-factor-toggle-container">
                    <div className="toggle-switch-wrapper">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={twoFactorEnabled}
                          onChange={(e) => {
                            const newStatus = e.target.checked;
                            handleToggleTwoFactor(newStatus);
                          }}
                          disabled={isUpdatingTwoFactor}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className="toggle-label">
                        {isUpdatingTwoFactor
                          ? t("members.profile.memberEditProfile.twoFactor.updating")
                          : twoFactorEnabled
                          ? t("members.profile.memberEditProfile.twoFactor.enabled")
                          : t("members.profile.memberEditProfile.twoFactor.disabled")}
                      </span>
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Date of Birth */}
            <div className="form-group">
              <label className="form-label" htmlFor="dob">
                {t("members.profile.memberEditProfile.fields.dateOfBirth")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                control={control}
                name="date_of_birth"
                render={({ onChange, onBlur, value, name, ref }) => (
                  <DateInput
                    onChange={(e) => {
                      onChange(e);
                    }}
                    onBlur={onBlur}
                    value={value}
                    maxDate={new Date()}
                    dateFormat={DATE_FORMAT}
                    inputRef={ref}
                    placeholder={t("members.profile.memberEditProfile.placeholders.dateOfBirth")}
                    id="dob"
                  />
                )}
              />
              {errors.date_of_birth && <div className="invalid-feedback">{errors.date_of_birth.message}</div>}
            </div>

            {/* Employee Code */}
            <div className="form-group">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.employeeCode")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="employeeCode"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <input
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    type="text"
                    className={`form-control ${errors.employeeCode ? "is-invalid" : ""}`}
                    placeholder={t("members.profile.memberEditProfile.placeholders.employeeCode")}
                  />
                )}
              />
              {errors.employeeCode && <div className="invalid-feedback">{errors.employeeCode.message}</div>}
            </div>

            {/* Marital Status */}
            <div className="form-group">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.maritalStatus")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="marital_status"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <select
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    className={`form-control ${errors.marital_status ? "is-invalid" : ""}`}
                  >
                    <option value="">{t("members.profile.memberEditProfile.dropdowns.selectStatus")}</option>
                    <option value="Single">
                      {t("members.profile.memberEditProfile.options.maritalStatus.single")}
                    </option>
                    <option value="Married">
                      {t("members.profile.memberEditProfile.options.maritalStatus.married")}
                    </option>
                    <option value="Divorced">
                      {t("members.profile.memberEditProfile.options.maritalStatus.divorced")}
                    </option>
                    <option value="Other">{t("members.profile.memberEditProfile.options.maritalStatus.other")}</option>
                  </select>
                )}
              />
              {errors.marital_status && <div className="invalid-feedback">{errors.marital_status.message}</div>}
            </div>

            {/* Religion */}
            <div className="form-group">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.religion")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="religion"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <select
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    className={`form-control ${errors.religion ? "is-invalid" : ""}`}
                  >
                    <option value="">{t("members.profile.memberEditProfile.dropdowns.selectReligion")}</option>
                    <option value="Christianity">
                      {t("members.profile.memberEditProfile.options.religion.christianity")}
                    </option>
                    <option value="Hinduism">{t("members.profile.memberEditProfile.options.religion.hinduism")}</option>
                    <option value="Islam">{t("members.profile.memberEditProfile.options.religion.islam")}</option>
                    <option value="Judaism">{t("members.profile.memberEditProfile.options.religion.judaism")}</option>
                    <option value="Buddhism">{t("members.profile.memberEditProfile.options.religion.buddhism")}</option>
                    <option value="Other">{t("members.profile.memberEditProfile.options.religion.other")}</option>
                  </select>
                )}
              />
              {errors.religion && <div className="invalid-feedback">{errors.religion.message}</div>}
            </div>

            {/* Home Town */}
            <div className="form-group">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.homeTown")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="home_town"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <input
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    type="text"
                    className={`form-control ${errors.home_town ? "is-invalid" : ""}`}
                    placeholder={t("members.profile.memberEditProfile.placeholders.homeTown")}
                  />
                )}
              />
              {errors.home_town && <div className="invalid-feedback">{errors.home_town.message}</div>}
            </div>

            {/* Nationality */}
            <div className="form-group">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.nationality")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="nationality"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <select
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    className={`form-control ${errors.nationality ? "is-invalid" : ""}`}
                    disabled={isLoadingCountries}
                  >
                    <option value="">
                      {isLoadingCountries
                        ? "Loading..."
                        : t("members.profile.memberEditProfile.dropdowns.selectNationality")}
                    </option>
                    {/* {countries.map((country) => (
                    <option key={country.id} value={country.name}>
                      {country.name}
                    </option>
                  ))} */}
                    <option value="American">
                      {t("members.profile.memberEditProfile.options.nationality.american")}
                    </option>
                    <option value="British">
                      {t("members.profile.memberEditProfile.options.nationality.british")}
                    </option>
                    <option value="Canadian">
                      {t("members.profile.memberEditProfile.options.nationality.canadian")}
                    </option>
                    <option value="Australian">
                      {t("members.profile.memberEditProfile.options.nationality.australian")}
                    </option>
                    <option value="Indian">{t("members.profile.memberEditProfile.options.nationality.indian")}</option>
                    <option value="Chinese">
                      {t("members.profile.memberEditProfile.options.nationality.chinese")}
                    </option>
                    <option value="Mexican">
                      {t("members.profile.memberEditProfile.options.nationality.mexican")}
                    </option>
                    <option value="Other">{t("members.profile.memberEditProfile.options.nationality.other")}</option>
                  </select>
                )}
              />
              {errors.nationality && <div className="invalid-feedback">{errors.nationality.message}</div>}
            </div>

            {/* Language */}
            <div className="form-group">
              <label className="form-label">
                {t("members.profile.memberEditProfile.fields.language")} <span className="mandatory-mark">*</span>
              </label>
              <Controller
                name="language"
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <select
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value || ""}
                    className={`form-control ${errors.language ? "is-invalid" : ""}`}
                    disabled={isLoadingLanguages}
                  >
                    <option value="">
                      {isLoadingLanguages
                        ? "Loading..."
                        : t("members.profile.memberEditProfile.dropdowns.selectLanguage")}
                    </option>
                    {/* {languages.map((language) => (
                    <option key={language.id} value={language.name}>
                      {language.name}
                    </option>
                  ))} */}

                    <option value="English">{t("members.profile.memberEditProfile.options.language.english")}</option>
                    <option value="Arabic">{t("members.profile.memberEditProfile.options.language.arabic")}</option>
                    <option value="Other">{t("members.profile.memberEditProfile.options.language.other")}</option>
                  </select>
                )}
              />
              {errors.language && <div className="invalid-feedback">{errors.language.message}</div>}
            </div>

            {/* Gender */}
            <div className="form-group">
              <label className="form-label">{t("members.profile.memberEditProfile.fields.gender")}</label>
              <div className="radio-group">
                <Controller
                  control={control}
                  name="gender"
                  render={({ onChange, value }) => (
                    <div className="radio-options" style={{ flexDirection: "row" }}>
                      <label className="radio-option">
                        <input
                          type="radio"
                          value="female"
                          checked={value === "female"}
                          onChange={() => onChange("female")}
                        />
                        {t("members.profile.memberEditProfile.options.gender.female")}
                      </label>
                      <label className="radio-option">
                        <input type="radio" value="male" checked={value === "male"} onChange={() => onChange("male")} />
                        {t("members.profile.memberEditProfile.options.gender.male")}
                      </label>
                      {/* <label className="radio-option">
                        <input
                          type="radio"
                          value="no-preference"
                          checked={value === "no-preference"}
                          onChange={() => onChange("no-preference")}
                        />
                        No Preference
                      </label> */}
                    </div>
                  )}
                />
              </div>
              {errors.gender && <div className="invalid-feedback">{errors.gender.message}</div>}
            </div>
          </div>

          {/* Address Fields */}
          <div className="form-group full-width">
            <label className="form-label">
              {t("members.profile.memberEditProfile.fields.permanentAddress")} <span className="mandatory-mark">*</span>
            </label>
            <Controller
              name="permanent_address"
              control={control}
              render={({ onChange, onBlur, value }) => (
                <textarea
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value || ""}
                  className={`form-control ${errors.permanent_address ? "is-invalid" : ""}`}
                  placeholder={t("members.profile.memberEditProfile.placeholders.permanentAddress")}
                  rows={3}
                />
              )}
            />
            {errors.permanent_address && <div className="invalid-feedback">{errors.permanent_address.message}</div>}
          </div>

          <div className="form-group full-width">
            <label className="form-label">
              {t("members.profile.memberEditProfile.fields.currentAddress")} <span className="mandatory-mark">*</span>
            </label>
            <Controller
              name="current_address"
              control={control}
              render={({ onChange, onBlur, value }) => (
                <textarea
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value || ""}
                  className={`form-control ${errors.current_address ? "is-invalid" : ""}`}
                  placeholder={t("members.profile.memberEditProfile.placeholders.currentAddress")}
                  rows={3}
                />
              )}
            />
            {errors.current_address && <div className="invalid-feedback">{errors.current_address.message}</div>}
          </div>
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <Link to={URLS.AUTH.VIEW_PROFILE}>
            <button type="button" className="btn-cancel">
              {t("members.profile.memberEditProfile.buttons.cancel")}
            </button>
          </Link>
          <button type="submit" className="btn-preview">
            {t("members.profile.memberEditProfile.buttons.save")}
          </button>
        </div>
      </form>

      {/* Profile Image Update Modal */}
      <ProfileImageUpdateModal
        isVisible={showImageUpdateModal}
        onCancel={handleCancelUpdate}
        onConfirm={handleConfirmUpdate}
      />
    </>
  );
};

export default MemberEditProfile;
